import { Conversation } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useConversation } from 'chat/conversations/queries/detail'
import { useUpdateConversation } from 'chat/conversations/queries/update'
import { EditConversationPropertyScreenProps } from 'home/stack/types'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { EditConversationDescriptionView } from './views/description'
import { EditConversationNameView } from './views/name'

export interface EditViewProps {
  value: string | undefined
  onChangeValue(value: string): void
}

export function EditConversationPropertyScreen({
  navigation,
  route,
}: EditConversationPropertyScreenProps) {
  const { conversationId, key } = route.params
  const conversationResult = useConversation(conversationId)
  const conversation = conversationResult?.data
  const [value, setValue] = useState<string | undefined>(conversation?.[key])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<Error>()
  const conversationUpdateMutation = useUpdateConversation(conversationId)
  const { theme } = useTheme()

  const save = useCallback(async () => {
    try {
      setSaving(true)
      await conversationUpdateMutation.mutateAsync({
        changes: { [key]: value },
      })
      navigation.pop()
    } catch (err: any) {
      setError(err)
      setSaving(false)
    }
  }, [conversationUpdateMutation, key, navigation, value])

  useLayoutEffect(() => {
    showHeaderTitle(navigation, key.charAt(0).toUpperCase() + key.slice(1))
    showHeaderButton({
      navigation,
      theme,
      title: t('done'),
      onPress: save,
      position: 'right',
      disabled: saving,
    })
  }, [key, navigation, save, saving, theme])

  const EditView = getView(key)

  return (
    <StatusBoundary
      loadingMessage={saving ? t('saving') : undefined}
      isLoading={conversationResult.isLoading || saving}
      error={conversationResult.error ?? error}
    >
      <EditView value={value} onChangeValue={setValue} />
    </StatusBoundary>
  )
}

function getView(key: keyof Conversation) {
  switch (key) {
    case 'name':
      return EditConversationNameView
    case 'description':
      return EditConversationDescriptionView
    default:
      throw new Error(`Property ${key} not supported`)
  }
}
